import ACTIONS from "../store/action";
import { formatPatientIdWithSite } from "./siteMapping";

/**
 * Enhances a URL with Paytient tracking parameters
 * @param {string} originalUrl - The original URL to enhance
 * @param {string} linkKey - The type of link (e.g., "program", "application", "provider")
 * @param {string|number} patientId - The patient ID for tracking
 * @param {string} environment - Environment name for site mapping
 * @returns {string} Enhanced URL with tracking parameters
 */
export const enhancePaytientUrl = (originalUrl, linkKey, patientId, environment) => {
  const supportedKeys = ["program", "application", "provider"];

  if (supportedKeys.includes(linkKey)) {
    const url = new URL(originalUrl);
    const params = new URLSearchParams(url.search);
    params.set("utm_source", "tailormed");

    const formattedPatientId = formatPatientIdWithSite(patientId, environment);
    params.set("utm_patientid", formattedPatientId);

    url.search = params.toString();
    return url.toString();
  }
  return originalUrl;
};

/**
 * Handles Paytient web link click tracking
 * @param {Object} assistanceProgram - The assistance program object
 * @param {Object} context - Context object containing user, patientId, dispatch, and environment
 * @param {string} linkKey - The type of link clicked
 * @param {string} websiteUrl - The website URL being clicked
 * @param {string} clickSource - Source identifier for tracking
 */
export const handlePaytientWebLinkClick = (
  assistanceProgram,
  { user, patientId, dispatch, environment },
  linkKey,
  websiteUrl,
  clickSource
) => {
  if (user && patientId) {
    const enhancedUrl = enhancePaytientUrl(websiteUrl, linkKey, patientId, environment);

    dispatch(
      ACTIONS.paytientWebLinkClickAction(user.id, patientId, {
        assistanceProgramId: assistanceProgram.id,
        assistanceProgramName: assistanceProgram.name,
        linkType: linkKey,
        websiteUrl: enhancedUrl,
        clickSource
      })
    );
  }
};

/**
 * Checks if an assistance program is a Paytient program
 * @param {Object} assistanceProgram - The assistance program object
 * @returns {boolean} True if it's a Paytient program
 */
export const isPaytientProgram = (assistanceProgram) => {
  return assistanceProgram?.pid?.toLowerCase().includes("paytient");
};

/**
 * Extracts patient ID from the current URL pathname
 * @returns {string|null} Patient ID if found, null otherwise
 */
export const getPatientIdFromUrl = () => {
  const patientIdMatch = window.location.pathname.match(/\/patient\/(\d+)/);
  return patientIdMatch ? patientIdMatch[1] : null;
};

/**
 * Creates a Paytient context object for tracking
 * @param {Object} user - User object
 * @param {string|number} patientId - Patient ID
 * @param {Function} dispatch - Redux dispatch function
 * @param {string} environment - Environment name for site mapping
 * @returns {Object} Context object for Paytient operations
 */
export const createPaytientContext = (user, patientId, dispatch, environment) => ({
  user,
  patientId,
  dispatch,
  environment
});

/**
 * Handles Paytient web link click with tracking and URL enhancement
 * @param {Object} assistanceProgram - The assistance program object
 * @param {Object} context - Context object containing user, patientId, dispatch, and environment
 * @param {string} linkKey - The type of link clicked
 * @param {string} originalUrl - The original URL being clicked
 * @param {string} clickSource - Source identifier for tracking
 * @returns {string} Enhanced URL with tracking parameters
 */
export const handlePaytientWebLinkClickAndEnhance = (assistanceProgram, context, linkKey, originalUrl, clickSource) => {
  // Track the click
  handlePaytientWebLinkClick(assistanceProgram, context, linkKey, originalUrl, clickSource);

  // Return enhanced URL
  return enhancePaytientUrl(originalUrl, linkKey, context.patientId, context.environment);
};
